@if (visible) {
  <section
    class="bg-white rounded-xl p-4 shadow-sm border border-slate-200/70"
    data-testid="data-extraction-agreement-card"
    aria-labelledby="data-extraction-agreement-card-heading"
  >
    <div class="flex items-start justify-between gap-3">
      <div>
        <h3
          id="data-extraction-agreement-card-heading"
          class="text-xs font-bold uppercase tracking-wider text-slate-500"
        >
          Data Extraction Agreement
        </h3>
        @if (agreement && isOfficial) {
          <p class="mt-1 text-[11px] text-slate-500">Official pre-moderation result</p>
        } @else if (agreement) {
          <p class="mt-1 text-[11px] text-slate-500">Preliminary result</p>
        }
      </div>
      @if (agreement) {
        <span
          class="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700"
        >
          {{ agreement.questions.length === 0 ? 'No questions' : 'Question metrics' }}
        </span>
      }
    </div>

    @if (loading) {
      <div class="mt-4 animate-pulse space-y-3" data-testid="de-agreement-loading">
        <div class="h-8 w-36 rounded bg-slate-200"></div>
        <div class="h-4 w-44 rounded bg-slate-100"></div>
        <div class="h-4 w-full rounded bg-slate-100"></div>
      </div>
    } @else if (errorMessage) {
      <div class="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        <p>{{ errorMessage }}</p>
        <button
          type="button"
          class="mt-2 rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-300"
          (click)="retry()"
          aria-label="Retry loading data extraction agreement statistics"
        >
          Retry
        </button>
      </div>
    } @else if (agreement) {
      @if (agreement.totalExtractionQuestions === 0) {
        <div class="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
          No extraction questions are available for agreement statistics.
        </div>
      } @else if (agreement.totalPairedAnswers === 0) {
        <div class="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
          No paired extraction answers are available yet.
        </div>
      } @else {
        <div class="mt-4">
          <div class="flex items-baseline gap-2">
            <span class="text-3xl font-extrabold text-slate-900">
              {{ formatPercent(agreement.overallExactAgreementPercentage) }}
            </span>
          </div>
          <p class="mt-1 text-sm font-semibold text-slate-700">overall exact agreement</p>

          <dl class="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Paired answers
              </dt>
              <dd class="font-bold text-slate-800">{{ agreement.totalPairedAnswers }}</dd>
            </div>
            <div>
              <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Questions
              </dt>
              <dd class="font-bold text-slate-800">
                {{ agreement.totalExtractionQuestions }} analyzed
              </dd>
            </div>
            <div>
              <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Categorical
              </dt>
              <dd class="font-bold text-slate-800">{{ categoricalQuestionCount }}</dd>
            </div>
            <div>
              <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Review
              </dt>
              <dd class="font-bold text-slate-800">
                {{ questionsRequiringReview }} question{{
                  questionsRequiringReview === 1 ? '' : 's'
                }}
              </dd>
            </div>
          </dl>

          @if (isOfficial && finalizedAt) {
            <p class="mt-3 text-xs text-slate-500">Finalized on {{ finalizedAt }}</p>
          }

          <button
            type="button"
            class="mt-4 text-xs font-semibold text-secondary hover:underline focus:outline-none focus:ring-2 focus:ring-secondary/40 rounded"
            (click)="detailsOpen = true"
          >
            View details
          </button>
        </div>
      }
    }
  </section>
}

@if (agreement) {
  <app-base-modal
    [open]="detailsOpen"
    (openChange)="detailsOpen = $event"
    title="Data Extraction Agreement Details"
    size="7xl"
  >
    <div class="space-y-5" data-testid="de-agreement-details-dialog">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {{ stageLabel() }}
          </p>
          <h3 class="text-xl font-bold text-slate-900">
            {{
              agreement.calculationType === 'OFFICIAL'
                ? 'Official pre-moderation result'
                : 'Preliminary result'
            }}
          </h3>
          @if (isOfficial && finalizedAt) {
            <p class="mt-1 text-sm text-slate-500">
              Finalized on {{ finalizedAt }} by {{ finalizedByUserName }}
            </p>
          }
        </div>
      </div>

      <div class="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-600">
        Cohen's kappa is used for categorical questions. Multi-choice, numeric and free-text
        questions use question-type-specific agreement measures.
      </div>

      <dl class="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Questions
          </dt>
          <dd class="text-lg font-bold text-slate-900">{{ agreement.totalExtractionQuestions }}</dd>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Paired answers
          </dt>
          <dd class="text-lg font-bold text-slate-900">{{ agreement.totalPairedAnswers }}</dd>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Overall exact
          </dt>
          <dd class="text-lg font-bold text-slate-900">
            {{ formatPercent(agreement.overallExactAgreementPercentage) }}
          </dd>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Categorical
          </dt>
          <dd class="text-lg font-bold text-slate-900">{{ categoricalQuestionCount }}</dd>
        </div>
      </dl>

      <div class="overflow-x-auto rounded-lg border border-slate-200">
        <table class="min-w-full divide-y divide-slate-200 text-sm">
          <thead
            class="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
          >
            <tr>
              <th class="px-3 py-2">Question</th>
              <th class="px-3 py-2">Type</th>
              <th class="px-3 py-2">Paired answers</th>
              <th class="px-3 py-2">Missing / unpaired</th>
              <th class="px-3 py-2">Metric</th>
              <th class="px-3 py-2">Result</th>
              <th class="px-3 py-2">Interpretation / status</th>
              <th class="px-3 py-2">Details</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 bg-white">
            @for (question of agreement.questions; track trackQuestion($index, question)) {
              <tr>
                <td class="px-3 py-2 font-semibold text-slate-800 max-w-xs">
                  {{ questionLabel(question) }}
                </td>
                <td class="px-3 py-2">{{ questionTypeLabel(question) }}</td>
                <td class="px-3 py-2">{{ question.pairedAnswerCount }}</td>
                <td class="px-3 py-2">{{ question.unpairedMissingCount }}</td>
                <td class="px-3 py-2">{{ metricLabel(question) }}</td>
                <td class="px-3 py-2">{{ resultSummary(question) }}</td>
                <td class="px-3 py-2">
                  <span>{{ questionStatus(question) }}</span>
                  @if (question.thresholdStatus) {
                    <span class="block text-xs font-semibold text-slate-500">
                      {{ thresholdStatusLabel(question.thresholdStatus) }}
                    </span>
                  }
                </td>
                <td class="px-3 py-2">
                  @if (isCategorical(question) && question.reviewerPairs.length > 0) {
                    <button
                      type="button"
                      class="rounded px-2 py-1 text-xs font-semibold text-secondary hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-secondary/40"
                      (click)="toggleQuestion(question)"
                      [attr.aria-expanded]="isExpanded(question)"
                      [attr.aria-controls]="'de-pair-details-' + question.questionId"
                    >
                      {{ isExpanded(question) ? 'Hide pairs' : 'View pairs' }}
                    </button>
                  } @else if (question.nonComputableReason === 'NOT_APPLICABLE') {
                    <span class="text-xs text-slate-500">Kappa not applicable</span>
                  } @else {
                    <span class="text-xs text-slate-500">No pair details</span>
                  }
                </td>
              </tr>
              @if (isExpanded(question)) {
                <tr [id]="'de-pair-details-' + question.questionId">
                  <td class="bg-slate-50 px-3 py-3" colspan="8">
                    <div class="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                      <table class="min-w-full divide-y divide-slate-200 text-sm">
                        <thead
                          class="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          <tr>
                            <th class="px-3 py-2">Reviewer pair</th>
                            <th class="px-3 py-2">Paired answers</th>
                            <th class="px-3 py-2">Agreements</th>
                            <th class="px-3 py-2">Disagreements</th>
                            <th class="px-3 py-2">Percent agreement</th>
                            <th class="px-3 py-2">Cohen's kappa</th>
                            <th class="px-3 py-2">Interpretation</th>
                            <th class="px-3 py-2">Threshold status</th>
                          </tr>
                        </thead>
                        <tbody class="divide-y divide-slate-100 bg-white">
                          @for (pair of question.reviewerPairs; track trackPair($index, pair)) {
                            <tr>
                              <td class="px-3 py-2 font-semibold text-slate-800">
                                {{ reviewerPairLabel(pair) }}
                              </td>
                              <td class="px-3 py-2">{{ pair.pairedAnswerCount }}</td>
                              <td class="px-3 py-2">{{ pair.agreementCount }}</td>
                              <td class="px-3 py-2">{{ pair.disagreementCount }}</td>
                              <td class="px-3 py-2">{{ formatPercent(pair.percentAgreement) }}</td>
                              <td class="px-3 py-2">{{ formatKappa(pair.cohenKappa) }}</td>
                              <td class="px-3 py-2">
                                {{ interpretationLabel(pair.interpretation) }}
                              </td>
                              <td class="px-3 py-2">
                                {{
                                  pair.thresholdStatus
                                    ? thresholdStatusLabel(pair.thresholdStatus)
                                    : 'Not available'
                                }}
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              }
            } @empty {
              <tr>
                <td class="px-3 py-4 text-slate-500" colspan="8">
                  No extraction questions are available for agreement statistics.
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </app-base-modal>
}
